/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, NgZone, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  AbstractFormComponent,
  BuilderNavigationService,
  CommonActions,
  CostCenterFinderDialogConfig,
  costCenterFinderDialogRoute,
  CostCenterFinderListItem,
  FormActionPipelines,
  FormFlowState,
  ReportService,
} from '@gh/builder-shared';
import {
  ActionFlow,
  AppRouter,
  FormStateSnapshot,
  getAllNewSelectedEntities,
  getFormStateSnapshotValue,
  getRouteInitData,
} from '@gh/common-shared';
import { withDisplayAsNameAndDescription } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import { DialogService, FormMode, yesAnswer, yesNoConfirmation } from '@gh/core-ui';
import {
  createDefaultTreeOptions,
  expandNodeById,
  goToNodeByPath,
  treeOptionsEnableDragging,
} from '@gh/core-ui-ext/tree';
import { openInNewWindow, toString } from '@gh/core-util';
import { filter$, first$, map$, mapTo$, switchMap$ } from '@gh/rx/operators';

import { ITreeState, TREE_ACTIONS, TreeComponent, TreeNode } from 'angular-tree-component';
import { first, isNil, sortBy } from 'lodash';

import { CSMasterBusinessService } from '../../resources/cs-master-business.service';
import {
  CSMasterCC,
  CSMasterCCInfo,
  CSMasterHeading,
  CSMasterHeadingInfo,
  CSMasterInfo,
  CSMasterInfoDetails,
  CSMasterItem,
  CSMasterItemInfo,
  CSMasterSelectionLevel,
  cSMasterSelectionToTreePath,
  CSMasterSubHeading,
  CSMasterSubHeadingInfo,
} from '../../resources/cs-master.model';
import { CSMasterDetailsFormService } from './cs-master-details-form.service';
import { GridComponent } from '@gh/core-ui-grid';

@Component({
  selector: 'gh-cs-master-details',
  template: require('./cs-master-details.component.html'),
  styles: [require('./cs-master-details.component.scss')],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    CSMasterDetailsFormService,
  ],
})
export class CSMasterDetailsComponent extends AbstractFormComponent<CSMasterInfoDetails> implements OnInit {

  treeOptions = createDefaultTreeOptions({
    idField: 'id',
    childrenField: 'children',
    allowDrag: (node) => node.level <= CSMasterSelectionLevel.Item,
    allowDrop: (node, { parent }) => node.level === parent.level + 1,
    nodeClass: (node) => node.level === 1 ? 'node-expandable' : '',
    // We disable selection on click and handle this behavior yourself.
    // Selection on click is not suitable when click on buttons.
    actionMapping: {
      mouse: {
        click: TREE_ACTIONS.ACTIVATE,
      },
    },
  });

  treeState: ITreeState = {};
  reportUrl: string;

  @ViewChild(TreeComponent, { static: true }) tree: TreeComponent;

  constructor(private route: ActivatedRoute,
              private changeDetectorRef: ChangeDetectorRef,
              private translations: TranslationService,
              private businessService: CSMasterBusinessService,
              public form: CSMasterDetailsFormService,
              private ngZone: NgZone,
              private dialogService: DialogService,
              private reportService: ReportService,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              private navigationService: BuilderNavigationService,
              private router: AppRouter) {
    super();
  }

  get title(): string {
    const masterInfo = getFormStateSnapshotValue(this.form.value.masterInfo);

    return {
      [FormMode.CreateMode]: this.translations.get('lbl_create_color_selection_master'),
      [FormMode.EditMode]:
        `${this.translations.get('lbl_edit_color_selection_master')}: ${masterInfo.name}`,
      [FormMode.ViewMode]:
        `${this.translations.get('lbl_color_selection_master')}: ${masterInfo.name}`,
    }[this.form.mode];
  }

  get aelTitle(): string {
    return {
      [FormMode.CreateMode]: 'lbl_create_color_selection_master',
      [FormMode.EditMode]: 'lbl_edit_color_selection_master',
      [FormMode.ViewMode]: 'lbl_color_selection_master',
    }[this.form.mode];
  }

  get masterInfo(): FormStateSnapshot<CSMasterInfo> {
    return this.form.value.masterInfo;
  }

  get headings(): CSMasterHeadingInfo[] {
    return this.form.value.headings;
  }

  get isHeadingSelected(): boolean {
    return this.form.selection.level === CSMasterSelectionLevel.Heading;
  }

  get isSubHeadingSelected(): boolean {
    return this.form.selection.level === CSMasterSelectionLevel.SubHeading;
  }

  get isItemSelected(): boolean {
    return this.form.selection.level === CSMasterSelectionLevel.Item;
  }

  get isCCSelected(): boolean {
    return this.form.selection.level === CSMasterSelectionLevel.CostCenter;
  }

  get currentHeading(): Maybe<CSMasterHeadingInfo> {
    return this.form.selection.heading;
  }

  get currentSubHeading(): Maybe<CSMasterSubHeadingInfo> {
    return this.form.selection.subHeading;
  }

  get currentItem(): Maybe<CSMasterItemInfo> {
    return this.form.selection.item;
  }

  get currentCC(): Maybe<CSMasterCCInfo> {
    return this.form.selection.costCenter;
  }

  get completeFlow(): ActionFlow<FormFlowState<CSMasterInfoDetails>> {
    return this.pipelines.complete({
      execute: this.commonActions.execute((state: FormFlowState<CSMasterInfoDetails>) =>
        this.businessService.save(state.value)),
      finish: (state: FormFlowState<CSMasterInfoDetails>) => {
        const { value } = state;
        this.form.resetDirty();
        if (this.form.createMode) {
          this.router.switchTo(['/setup/job-admin/cs-masters', value.id]);
        } else {
          this.form.merge(value);
        }
      },
    });
  }

  ngOnInit(): void {
    const { details, reportUrl } = getRouteInitData(this.route);

    this.form.selectionChanges.pipe(
      map$(cSMasterSelectionToTreePath),
      filter$(Boolean),
      // wait while all processing of new/removed nodes is completed by tree component
      switchMap$((path) => this.ngZone.onMicrotaskEmpty.pipe(first$(), mapTo$(path))))
      .subscribe((path: any) => {
        this.treeState = goToNodeByPath(this.treeState, path);
        this.changeDetectorRef.markForCheck();
      });

    this.form.build(details);

    this.treeOptions = treeOptionsEnableDragging(this.treeOptions, this.form.editable);
    this.reportUrl = reportUrl;

    this.form.changes.subscribe(() => this.changeDetectorRef.markForCheck());
  }

  onMasterInfoChange(masterInfo: FormStateSnapshot<CSMasterInfo>): void {
    this.form.updateMasterInfo(masterInfo);
  }

  onCurrentHeadingDataChange(headingData: FormStateSnapshot<CSMasterHeading>): void {
    const { currentHeading } = this;

    if (currentHeading) {
      this.form.updateHeadingData(currentHeading, headingData);
    }
  }

  onCurrentSubHeadingDataChange(subHeadingData: FormStateSnapshot<CSMasterSubHeading>): void {
    const { currentSubHeading } = this;

    if (currentSubHeading) {
      this.form.updateSubHeadingData(currentSubHeading, subHeadingData);
    }
  }

  onCurrentItemDataChange(itemData: FormStateSnapshot<CSMasterItem>): void {
    const { currentItem } = this;

    if (currentItem) {
      this.form.updateItemData(currentItem, itemData);
    }
  }

  onCurrentCCDataChange(costCenterData: FormStateSnapshot<CSMasterCC>): void {
    const { currentCC } = this;

    if (currentCC) {
      this.form.updateCCData(currentCC, costCenterData);
      // update tree because order of CCs could be changed
      this.updateTree();
    }
  }

  onSelectNode(node: TreeNode): void {
    switch (node.level) {
      case CSMasterSelectionLevel.Heading:
        this.form.selectHeading(node.data);
        break;
      case CSMasterSelectionLevel.SubHeading:
        this.form.selectSubHeading(node.parent.data, node.data);
        break;
      case CSMasterSelectionLevel.Item:
        this.form.selectItem(node.parent.parent.data, node.parent.data, node.data);
        break;
      case CSMasterSelectionLevel.CostCenter:
        this.form.selectCC(node.parent.parent.parent.data, node.parent.parent.data, node.parent.data, node.data);
        break;
      default:
        break;
    }
  }

  onMoveNode(): void {
    this.form.moveNodes();
  }

  onHeadingAdd(): void {
    const newHeading = this.businessService.createNewHeading();
    this.form.addHeading(newHeading);

    this.updateTree();

    this.form.selectHeading(newHeading);
  }

  onSubHeadingAdd(headingNode: TreeNode): void {
    const heading: CSMasterHeadingInfo = headingNode.data;
    const newSubHeading = this.businessService.createNewSubHeading();
    this.form.addSubHeading(heading, newSubHeading);

    this.updateTree();

    this.treeState = expandNodeById(this.treeState, heading.id);
    this.form.selectSubHeading(heading, newSubHeading);
  }

  onItemAdd(subHeadingNode: TreeNode): void {
    const heading: CSMasterHeadingInfo = subHeadingNode.parent.data;
    const subHeading: CSMasterSubHeadingInfo = subHeadingNode.data;

    const newItem = this.businessService.createNewItem();
    this.form.addItem(subHeading, newItem);

    this.updateTree();

    this.treeState = expandNodeById(this.treeState, subHeading.id);
    this.form.selectItem(heading, subHeading, newItem);
  }

  onCCAdd(itemNode: TreeNode): void {
    const heading: CSMasterHeadingInfo = itemNode.parent.parent.data;
    const subHeading: CSMasterSubHeadingInfo = itemNode.parent.data;
    const item: CSMasterItemInfo = itemNode.data;

    this.dialogService.openWithInput(costCenterFinderDialogRoute, <CostCenterFinderDialogConfig>{
      multiselection: true,
      preselected: item.children
        .map((cc) => getFormStateSnapshotValue(cc.info).costCenter)
        .filter(Boolean)
        .map((ccItem) => ccItem.id),
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getAllNewSelectedEntities))
      .subscribe((costCenters: CostCenterFinderListItem[]) => {
        const newCostCenters = sortBy(costCenters, 'code')
          .map((cc) => this.businessService.createNewCC(withDisplayAsNameAndDescription({
            id: cc.id,
            name: toString(cc.code),
            description: cc.name,
          })));
        this.form.addCostCenters(item, newCostCenters);

        this.updateTree();

        this.treeState = expandNodeById(this.treeState, item.id);
        // tslint:disable-next-line:no-non-null-assertion
        this.form.selectCC(heading, subHeading, item, first(newCostCenters)!);
      });
  }

  onHeadingRemove(headingNode: TreeNode): void {
    const heading: CSMasterHeadingInfo = headingNode.data;
    this.dialogService.confirmation(yesNoConfirmation('msg_cs_heading_delete')).pipe(
      filter$(yesAnswer))
      .subscribe(() => {
        this.form.removeHeading(heading);
        this.updateTree();
      });
  }

  onSubHeadingRemove(subHeadingNode: TreeNode): void {
    const heading: CSMasterHeadingInfo = subHeadingNode.parent.data;
    const subHeading: CSMasterSubHeadingInfo = subHeadingNode.data;
    this.dialogService.confirmation(yesNoConfirmation('msg_cs_sub_heading_delete')).pipe(
      filter$(yesAnswer))
      .subscribe(() => {
        this.form.removeSubHeading(heading, subHeading);
        this.updateTree();
      });
  }

  onItemRemove(itemNode: TreeNode): void {
    const heading: CSMasterHeadingInfo = itemNode.parent.parent.data;
    const subHeading: CSMasterSubHeadingInfo = itemNode.parent.data;
    const item: CSMasterItemInfo = itemNode.data;
    this.dialogService.confirmation(yesNoConfirmation('msg_cs_item_delete')).pipe(
      filter$(yesAnswer))
      .subscribe(() => {
        this.form.removeItem(heading, subHeading, item);
        this.updateTree();
      });
  }

  onCCRemove(ccNode: TreeNode): void {
    const heading: CSMasterHeadingInfo = ccNode.parent.parent.parent.data;
    const subHeading: CSMasterSubHeadingInfo = ccNode.parent.parent.data;
    const item: CSMasterItemInfo = ccNode.parent.data;
    const cc: CSMasterCCInfo = ccNode.data;
    this.dialogService.confirmation(yesNoConfirmation('msg_cs_cc_delete')).pipe(
      filter$(yesAnswer))
      .subscribe(() => {
        this.form.removeCC(heading, subHeading, item, cc);
        this.updateTree();
      });
  }

  onReport(): void {
    openInNewWindow(this.reportUrl);
  }

  goToAel(): void {
    this.navigationService.goToAel({
      entityId: this.form.id,
      entityType: 'CS_MASTER',
      label: this.aelTitle,
    });
  }

  getHeadingTitle(heading: CSMasterHeadingInfo): string {
    return getFormStateSnapshotValue(heading.info).name;
  }

  getSubHeadingTitle(subHeading: CSMasterSubHeadingInfo): string {
    return getFormStateSnapshotValue(subHeading.info).name;
  }

  getItemTitle(item: CSMasterItemInfo): string {
    return getFormStateSnapshotValue(item.info).name;
  }

  getCCTitle(costCenter: CSMasterCCInfo): string {
    const costCenterData = getFormStateSnapshotValue(costCenter.info).costCenter;

    return isNil(costCenterData) ? '' : costCenterData.display || '';
  }

  isCreateMode(): boolean {
    return this.form.mode === FormMode.CreateMode;
  }

  get headingSequence(): number {
    return (isNil(this.currentHeading) ? 0 : this.form.value.headings.indexOf(this.currentHeading)) + 1;
  }

  get subHeadingSequence(): number {
    return (isNil(this.currentHeading) || isNil(this.currentSubHeading) ?
      0 : this.currentHeading.children.indexOf(this.currentSubHeading)) + 1;
  }

  private updateTree(): void {
    this.tree.treeModel.update();
  }
}
